/**
 * Dealer-portal theming.
 *
 * A theme is a full PALETTE derived from two inputs — a background color and an
 * accent color — plus everything in between (sidebar, cards, inputs, borders,
 * text, scrollbars) computed by lightening/darkening the background and picking
 * readable text via luminance. This makes every preset coherent and lets a
 * fully-custom theme (pick background + accent) look just as finished.
 *
 * - PRESETS: 11 named full palettes (background + accent).
 * - Custom: user picks background + accent; the rest is derived.
 * - The base `.dark`/`.light` class is derived from background luminance so the
 *   existing token fallbacks + Tailwind `dark:` variants stay sensible.
 * - The accent also feeds RGB triplets that drive the brand-weight Tailwind
 *   yellow shades (400/500/600) + keder-yellow (~850 spots).
 *
 * Persistence: localStorage for instant, flash-free boot; the API
 * (`/dealers/my/ui-theme`) is the durable per-user copy across devices.
 */

export type Mode = 'dark' | 'light';

export interface Preset {
  id: string;
  label: string;
  bg: string;
  accent: string;
}

/** 11 preset palettes (8 dark, 3 light) + Custom. */
export const PRESETS: Preset[] = [
  { id: 'keder-dark', label: 'Keder Dark', bg: '#0a0a0a', accent: '#FFCD24' },
  { id: 'midnight', label: 'Midnight', bg: '#0f172a', accent: '#818cf8' },
  { id: 'ocean', label: 'Ocean', bg: '#0c1a26', accent: '#22d3ee' },
  { id: 'forest', label: 'Forest', bg: '#0c1f17', accent: '#34d399' },
  { id: 'plum', label: 'Plum', bg: '#1a1023', accent: '#c084fc' },
  { id: 'crimson', label: 'Crimson', bg: '#17090c', accent: '#fb7185' },
  { id: 'graphite', label: 'Graphite', bg: '#18181b', accent: '#f59e0b' },
  { id: 'espresso', label: 'Espresso', bg: '#1c1512', accent: '#e0a458' },
  { id: 'keder-light', label: 'Keder Light', bg: '#f9fafb', accent: '#FFCD24' },
  { id: 'paper', label: 'Paper', bg: '#ffffff', accent: '#2563eb' },
  { id: 'mint', label: 'Mint', bg: '#eefdf6', accent: '#0d9488' },
];

export interface ThemePref {
  preset: string; // preset id OR 'custom'
  customBg: string; // used when preset === 'custom'
  customAccent: string; // used when preset === 'custom'
}

export const DEFAULT_THEME: ThemePref = { preset: 'keder-dark', customBg: '#0a0a0a', customAccent: '#FFCD24' };
export const DEFAULT_LIGHT: ThemePref = { preset: 'keder-light', customBg: '#f9fafb', customAccent: '#FFCD24' };
const STORAGE_KEY = 'ui_theme';
const LEGACY_KEY = 'theme';
const slotKey = (m: Mode) => `ui_theme_${m}`; // remembered per-mode theme

// ── color helpers ────────────────────────────────────────────────────────────
function channels(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return [parseInt(full.slice(0, 2), 16) || 0, parseInt(full.slice(2, 4), 16) || 0, parseInt(full.slice(4, 6), 16) || 0];
}
function toHex(r: number, g: number, b: number): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}
/** Blend hex toward a target color by amt (0..1). */
function mix(hex: string, target: [number, number, number], amt: number): string {
  const [r, g, b] = channels(hex);
  return toHex(r + (target[0] - r) * amt, g + (target[1] - g) * amt, b + (target[2] - b) * amt);
}
const lighten = (hex: string, amt: number) => mix(hex, [255, 255, 255], amt);
const darken = (hex: string, amt: number) => mix(hex, [0, 0, 0], amt);
function luminance(hex: string): number {
  const [r, g, b] = channels(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}
export function contrastText(hex: string): string {
  return luminance(hex) > 0.6 ? '#000000' : '#ffffff';
}
function triplet(hex: string): string {
  const [r, g, b] = channels(hex);
  return `${r} ${g} ${b}`;
}
export function isValidHex(v?: string | null): v is string {
  return !!v && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v);
}

/** Resolve a pref to its background + accent (preset table wins for named presets). */
export function resolveColors(pref: ThemePref): { bg: string; accent: string } {
  const p = PRESETS.find((x) => x.id === pref.preset);
  return {
    bg: p ? p.bg : (isValidHex(pref.customBg) ? pref.customBg : '#0a0a0a'),
    accent: p ? p.accent : (isValidHex(pref.customAccent) ? pref.customAccent : '#FFCD24'),
  };
}

export function resolveMode(pref: ThemePref): Mode {
  return luminance(resolveColors(pref).bg) > 0.5 ? 'light' : 'dark';
}

/** Full CSS-variable map derived from background + accent. */
function palette(bg: string, accent: string): Record<string, string> {
  const mode: Mode = luminance(bg) > 0.5 ? 'light' : 'dark';
  const accentText = contrastText(accent);
  const accentHover = mode === 'dark' ? lighten(accent, 0.18) : darken(accent, 0.12);

  const chrome = mode === 'dark'
    ? {
        '--bg-page': bg,
        '--bg-sidebar': darken(bg, 0.2),
        '--bg-card': lighten(bg, 0.06),
        '--bg-input': lighten(bg, 0.05),
        '--bg-header': lighten(bg, 0.06),
        '--bg-hover': lighten(bg, 0.12),
        '--bg-table-header': lighten(bg, 0.05),
        '--bg-table-row-hover': lighten(bg, 0.09),
        '--border-color': lighten(bg, 0.14),
        '--border-input': lighten(bg, 0.2),
        '--text-primary': '#ffffff',
        '--text-secondary': lighten(bg, 0.55),
        '--text-muted': lighten(bg, 0.4),
        '--sidebar-text': lighten(bg, 0.62),
        '--sidebar-text-hover': '#ffffff',
        '--scrollbar-bg': bg,
        '--scrollbar-thumb': 'rgba(255,255,255,0.2)',
        '--scrollbar-thumb-hover': 'rgba(255,255,255,0.32)',
      }
    : {
        '--bg-page': bg,
        '--bg-sidebar': '#ffffff',
        '--bg-card': '#ffffff',
        '--bg-input': '#ffffff',
        '--bg-header': '#ffffff',
        '--bg-hover': darken(bg, 0.05),
        '--bg-table-header': darken(bg, 0.03),
        '--bg-table-row-hover': darken(bg, 0.04),
        '--border-color': darken(bg, 0.1),
        '--border-input': darken(bg, 0.16),
        '--text-primary': '#111827',
        '--text-secondary': '#6b7280',
        '--text-muted': '#9ca3af',
        '--sidebar-text': '#374151',
        '--sidebar-text-hover': '#111827',
        '--scrollbar-bg': darken(bg, 0.04),
        '--scrollbar-thumb': 'rgba(0,0,0,0.2)',
        '--scrollbar-thumb-hover': 'rgba(0,0,0,0.3)',
      };

  return {
    ...chrome,
    '--accent': accent,
    '--accent-hover': accentHover,
    '--accent-text': accentText,
    '--input-focus': accent,
    '--sidebar-active-bg': accent,
    '--sidebar-active-text': accentText,
    '--accent-rgb': triplet(accent),
    '--accent-lt-rgb': triplet(lighten(accent, 0.22)),
    '--accent-dk-rgb': triplet(darken(accent, 0.15)),
  };
}

/** Preview swatches for a preset card [page, surface, accent]. */
export function previewSwatch(bg: string, accent: string): { page: string; sidebar: string; card: string; accent: string } {
  const p = palette(bg, accent);
  return { page: p['--bg-page'], sidebar: p['--bg-sidebar'], card: p['--bg-card'], accent };
}

/** Apply a theme to the document. Safe to call repeatedly. */
export function applyTheme(pref: ThemePref): void {
  const root = document.documentElement;
  const { bg, accent } = resolveColors(pref);
  const mode: Mode = luminance(bg) > 0.5 ? 'light' : 'dark';

  root.classList.remove('dark', 'light', 'dark-theme', 'light-theme');
  root.classList.add(mode);
  root.removeAttribute('data-theme');

  const vars = palette(bg, accent);
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
}

/** Coerce any stored/remote value (current OR legacy shape) into a ThemePref. */
export function normalizePref(raw: any): ThemePref {
  if (raw && typeof raw === 'object') {
    // Current shape
    if (typeof raw.preset === 'string') {
      return {
        preset: raw.preset,
        customBg: isValidHex(raw.customBg) ? raw.customBg : '#0a0a0a',
        customAccent: isValidHex(raw.customAccent) ? raw.customAccent : '#FFCD24',
      };
    }
    // Legacy: { mode, darkAccent, lightAccent }
    if (raw.mode || raw.darkAccent || raw.lightAccent) {
      const mode: Mode = raw.mode === 'light' ? 'light' : 'dark';
      const accent = (mode === 'dark' ? raw.darkAccent : raw.lightAccent) || '#FFCD24';
      const isDefault = accent.toLowerCase() === '#ffcd24';
      if (isDefault) return { ...DEFAULT_THEME, preset: mode === 'light' ? 'keder-light' : 'keder-dark' };
      return { preset: 'custom', customBg: mode === 'light' ? '#f9fafb' : '#0a0a0a', customAccent: accent };
    }
    // Oldest: { theme, accent, customBase }
    if (raw.theme) {
      const map: Record<string, string> = { 'keder-dark': 'keder-dark', 'keder-light': 'keder-light', slate: 'midnight', ocean: 'ocean' };
      if (raw.theme === 'custom' && isValidHex(raw.accent)) {
        return { preset: 'custom', customBg: raw.customBase === 'light' ? '#f9fafb' : '#0a0a0a', customAccent: raw.accent };
      }
      return { ...DEFAULT_THEME, preset: map[raw.theme] || 'keder-dark' };
    }
  }
  return { ...DEFAULT_THEME };
}

export function loadThemePref(): ThemePref {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return normalizePref(JSON.parse(raw));
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === 'light') return { ...DEFAULT_THEME, preset: 'keder-light' };
    if (legacy === 'dark') return { ...DEFAULT_THEME, preset: 'keder-dark' };
  } catch {
    /* ignore */
  }
  return { ...DEFAULT_THEME };
}

export function saveThemePrefLocal(pref: ThemePref): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pref));
    localStorage.setItem(LEGACY_KEY, resolveMode(pref));
    // Remember this as the go-to theme for its mode, so the top-bar toggle can
    // restore it later instead of reverting to a default.
    localStorage.setItem(slotKey(resolveMode(pref)), JSON.stringify(pref));
  } catch {
    /* ignore */
  }
  applyTheme(pref);
}

/** The user's remembered theme for a given mode, if any. */
export function getModeSlot(mode: Mode): ThemePref | null {
  try {
    const raw = localStorage.getItem(slotKey(mode));
    return raw ? normalizePref(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

/**
 * What the top-bar toggle should switch to: the user's remembered theme for the
 * OPPOSITE mode (falling back to the Keder default for that mode). This keeps a
 * light/dark flip from ever discarding a chosen preset/custom theme.
 */
export function oppositeModePref(current: ThemePref): ThemePref {
  const target: Mode = resolveMode(current) === 'dark' ? 'light' : 'dark';
  return getModeSlot(target) || (target === 'light' ? { ...DEFAULT_LIGHT } : { ...DEFAULT_THEME });
}
